/**
 * VendorAPPage.js  –  Vendor Master + Accounts Payable
 * Supplier profiles · AP Invoices · Payment recording · Ageing report
 * UI v2 — improved form UX with section grouping, visual cues, better layout
 */
import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader, Card, TabBar, Field, inputStyle, btnPrimary, btnSm, Empty, fmtKES } from '../components/UI';
import * as api from '../api/client';

const gold   = '#C9A84C';
const green  = '#2ecc71';
const red    = '#e74c3c';
const orange = '#f39c12';
const blue   = '#3498db';

const VENDOR_CATS = [
  { value: 'food_bev',    label: '🍽️  Food & Beverage' },
  { value: 'cleaning',    label: '🧹  Cleaning & Hygiene' },
  { value: 'maintenance', label: '🔧  Maintenance & Repairs' },
  { value: 'it',          label: '💻  IT & Technology' },
  { value: 'utilities',   label: '⚡  Utilities' },
  { value: 'transport',   label: '🚗  Transport & Logistics' },
  { value: 'laundry',     label: '👕  Laundry & Linen' },
  { value: 'other',       label: '📦  Other' },
];

const PAYMENT_METHODS = [
  { value: 'bank',   label: '🏦 Bank Transfer' },
  { value: 'mpesa',  label: '📱 M-Pesa' },
  { value: 'cash',   label: '💵 Cash' },
  { value: 'cheque', label: '📄 Cheque' },
];

const statusColor = { unpaid: red, partial: orange, paid: green, disputed: '#888' };
const statusIcon  = { unpaid: '🔴', partial: '🟡', paid: '🟢', disputed: '⚫' };

const blankVendor = {
  name: '', contact_person: '', phone: '', email: '', address: '',
  kra_pin: '', payment_terms_days: 30, bank_name: '', bank_account: '',
  category: 'other', notes: '',
};
const blankInvoice = {
  vendor: '', invoice_number: '', invoice_date: new Date().toISOString().split('T')[0],
  due_date: '', amount: '', description: '',
};

/* ─── Shared modal shell ──────────────────────────────────────────────── */
function Modal({ onClose, children, width = 600 }) {
  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
        zIndex: 999, display: 'flex', alignItems: 'center',
        justifyContent: 'center', overflow: 'auto', padding: 20,
      }}
    >
      <div style={{
        background: 'var(--surface)', borderRadius: 16, width, maxWidth: '95vw',
        boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
        border: '1px solid rgba(201,168,76,0.15)',
        animation: 'slideUp 0.18s ease',
      }}>
        {children}
      </div>
      <style>{`@keyframes slideUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}

/* ─── Form section grouper ────────────────────────────────────────────── */
function FormSection({ icon, title, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        paddingBottom: 8, borderBottom: `1px solid rgba(201,168,76,0.2)`,
        marginBottom: 14,
      }}>
        <span style={{ fontSize: 15 }}>{icon}</span>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.09em', color: gold, textTransform: 'uppercase' }}>{title}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {children}
      </div>
    </div>
  );
}

const iStyle = { ...inputStyle, borderRadius: 8, padding: '10px 12px', fontSize: 14 };
const iStyleErr = { ...iStyle, borderColor: '#ef4444', background: 'rgba(239,68,68,0.04)' };

function FInput({ label, required, hint, span, value, onChange, placeholder, type = 'text', min, step, autoFocus, error }) {
  return (
    <div style={{ gridColumn: span === 2 ? '1/-1' : undefined }}>
      <Field label={label} required={required} hint={hint} error={error}>
        <input type={type} value={value || ''} onChange={onChange} placeholder={placeholder}
          min={min} step={step} autoFocus={autoFocus} style={error ? iStyleErr : iStyle} />
      </Field>
    </div>
  );
}

function FSelect({ label, required, hint, span, value, onChange, children, error }) {
  return (
    <div style={{ gridColumn: span === 2 ? '1/-1' : undefined }}>
      <Field label={label} required={required} hint={hint} error={error}>
        <select value={value || ''} onChange={onChange} style={{ ...(error ? iStyleErr : iStyle), cursor: 'pointer' }}>
          {children}
        </select>
      </Field>
    </div>
  );
}

/* ─── General API error banner ─────────────────────────────────────────── */
function ErrorBanner({ errors }) {
  const general = errors?._general || errors?.non_field_errors || errors?.detail;
  if (!general) return null;
  return (
    <div style={{
      margin: '0 0 16px', padding: '10px 14px', borderRadius: 8,
      background: 'rgba(239,68,68,0.07)', border: '1.5px solid rgba(239,68,68,0.25)',
      display: 'flex', alignItems: 'flex-start', gap: 9, fontSize: 13, color: '#ef4444',
    }}>
      <span style={{ fontSize: 15, flexShrink: 0 }}>⚠️</span>
      <span>{general}</span>
    </div>
  );
}

/* ─── Modal chrome ────────────────────────────────────────────────────── */
function ModalHeader({ icon, title, subtitle, onClose }) {
  return (
    <div style={{
      padding: '22px 24px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)',
      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <span style={{ fontSize: 22 }}>{icon}</span>
          <h3 style={{ margin: 0, color: gold, fontSize: 18, fontWeight: 700 }}>{title}</h3>
        </div>
        {subtitle && <div style={{ color: 'var(--textMuted)', fontSize: 13, paddingLeft: 32 }}>{subtitle}</div>}
      </div>
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--textMuted)', cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: 4 }}>✕</button>
    </div>
  );
}

function ModalFooter({ onSave, onCancel, saveLabel = 'Save', saving = false, errorCount = 0 }) {
  return (
    <div style={{
      padding: '16px 24px', borderTop: '1px solid rgba(255,255,255,0.06)',
      display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center',
    }}>
      {errorCount > 0 && (
        <div style={{
          marginRight: 'auto', display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 12, color: '#ef4444', fontWeight: 600,
          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
          borderRadius: 6, padding: '4px 10px',
        }}>
          <span>⚠</span>
          <span>{errorCount} field{errorCount > 1 ? 's need' : ' needs'} attention</span>
        </div>
      )}
      <button onClick={onCancel} style={{ ...btnSm, minWidth: 90 }}>Cancel</button>
      <button onClick={onSave} disabled={saving} style={{ ...btnPrimary, minWidth: 140, opacity: saving ? 0.7 : 1 }}>
        {saving ? '⟳ Saving…' : saveLabel}
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */

export default function VendorAPPage({ notify, userRole }) {
  const [tab, setTab]           = useState('vendors');
  const [vendors, setVendors]   = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [ageing, setAgeing]     = useState(null);
  const [loading, setLoading]   = useState(true);

  const [showVendorForm, setShowVendorForm] = useState(false);
  const [editVendor, setEditVendor]         = useState(null);
  const [vendorForm, setVendorForm]         = useState(blankVendor);
  const [savingVendor, setSavingVendor]     = useState(false);
  const [vendorErrors, setVendorErrors]     = useState({});

  const [showInvForm, setShowInvForm] = useState(false);
  const [invForm, setInvForm]         = useState(blankInvoice);
  const [savingInv, setSavingInv]     = useState(false);
  const [invErrors, setInvErrors]     = useState({});

  const [showPayForm, setShowPayForm] = useState(false);
  const [payInvoice, setPayInvoice]   = useState(null);
  const [payForm, setPayForm]         = useState({ amount: '', method: 'bank', reference: '', notes: '', payment_date: new Date().toISOString().split('T')[0] });
  const [savingPay, setSavingPay]     = useState(false);
  const [payErrors, setPayErrors]     = useState({});

  const [vendorFilter, setVendorFilter] = useState('');
  const [invFilter, setInvFilter]       = useState({ status: '', vendor: '' });

  const canEdit = ['admin', 'accountant'].includes(userRole);

  const vf = (k, v) => setVendorForm(p => ({ ...p, [k]: v }));
  const inf = (k, v) => setInvForm(p => ({ ...p, [k]: v }));
  const pf  = (k, v) => setPayForm(p => ({ ...p, [k]: v }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [vRes, iRes] = await Promise.all([api.getVendors(), api.getAPInvoices()]);
      setVendors(vRes.data.results || vRes.data || []);
      setInvoices(iRes.data.results || iRes.data || []);
    } catch { notify?.('Failed to load vendor data', 'error'); }
    finally { setLoading(false); }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (tab === 'ageing') {
      setAgeing(null);
      api.getAPAgeing()
        .then(r => {
          if (r.data?.error) notify?.(r.data.error, 'error');
          else setAgeing(r.data);
        })
        .catch(err => {
          const msg = err?.response?.data?.error || 'Failed to load ageing report — check that all migrations have been applied.';
          notify?.(msg, 'error');
        });
    }
  }, [tab]);

  /* ── helpers ── */
  const extractApiErrors = (err) => {
    const data = err?.response?.data;
    if (!data) return {};
    if (typeof data === 'string') return { _general: data };
    if (typeof data === 'object') {
      // Django REST returns { field: ["error msg", ...], non_field_errors: [...] }
      const mapped = {};
      Object.entries(data).forEach(([k, v]) => {
        mapped[k] = Array.isArray(v) ? v[0] : String(v);
      });
      return mapped;
    }
    return {};
  };

  const saveVendor = async () => {
    const errs = {};
    if (!vendorForm.name.trim()) errs.name = 'Vendor name is required';
    if (vendorForm.email && !/\S+@\S+\.\S+/.test(vendorForm.email)) errs.email = 'Enter a valid email address';
    if (vendorForm.payment_terms_days && isNaN(Number(vendorForm.payment_terms_days))) errs.payment_terms_days = 'Must be a number';
    if (Object.keys(errs).length) { setVendorErrors(errs); return; }
    setVendorErrors({});
    setSavingVendor(true);
    try {
      if (editVendor) { await api.updateVendor(editVendor.id, vendorForm); notify?.('Vendor updated'); }
      else            { await api.createVendor(vendorForm); notify?.('Vendor added successfully'); }
      setShowVendorForm(false); setEditVendor(null); setVendorForm(blankVendor); load();
    } catch (err) {
      const apiErrs = extractApiErrors(err);
      if (Object.keys(apiErrs).length) {
        setVendorErrors(apiErrs);
        notify?.('Please fix the highlighted fields', 'error');
      } else {
        notify?.('Failed to save vendor', 'error');
      }
    }
    finally { setSavingVendor(false); }
  };

  const saveInvoice = async () => {
    const errs = {};
    if (!invForm.vendor) errs.vendor = 'Please select a vendor';
    if (!invForm.invoice_number?.trim()) errs.invoice_number = 'Invoice number is required';
    if (!invForm.amount || isNaN(Number(invForm.amount)) || Number(invForm.amount) <= 0) errs.amount = 'Enter a valid positive amount';
    if (!invForm.invoice_date) errs.invoice_date = 'Invoice date is required';
    if (!invForm.due_date) errs.due_date = 'Due date is required';
    if (invForm.invoice_date && invForm.due_date && invForm.due_date < invForm.invoice_date) errs.due_date = 'Due date must be after invoice date';
    if (Object.keys(errs).length) { setInvErrors(errs); return; }
    setInvErrors({});
    setSavingInv(true);
    try {
      await api.createAPInvoice(invForm);
      notify?.('Invoice recorded');
      setShowInvForm(false); setInvForm(blankInvoice); load();
    } catch (err) {
      const apiErrs = extractApiErrors(err);
      if (Object.keys(apiErrs).length) {
        setInvErrors(apiErrs);
        notify?.('Please fix the highlighted fields', 'error');
      } else {
        notify?.('Failed to save invoice', 'error');
      }
    }
    finally { setSavingInv(false); }
  };

  const recordPayment = async () => {
    const errs = {};
    if (!payForm.amount) errs.amount = 'Amount is required';
    const amt = parseFloat(payForm.amount);
    if (payForm.amount && (isNaN(amt) || amt <= 0)) errs.amount = 'Enter a valid positive amount';
    if (payForm.amount && !isNaN(amt) && amt > parseFloat(payInvoice.balance_due)) errs.amount = `Cannot exceed balance due (${fmtKES(payInvoice.balance_due)})`;
    if (!payForm.method) errs.method = 'Payment method is required';
    if (!payForm.payment_date) errs.payment_date = 'Payment date is required';
    if (Object.keys(errs).length) { setPayErrors(errs); return; }
    setPayErrors({});
    setSavingPay(true);
    try {
      await api.createAPPayment({
        invoice: payInvoice.id,
        amount: amt.toFixed(2),
        method: payForm.method,
        reference: payForm.reference || '',
        notes: payForm.notes || '',
        payment_date: payForm.payment_date || new Date().toISOString().split('T')[0],
      });
      notify?.('Payment recorded successfully');
      setShowPayForm(false); setPayInvoice(null);
      setPayForm({ amount: '', method: 'bank', reference: '', notes: '', payment_date: new Date().toISOString().split('T')[0] }); load();
    } catch (err) {
      const apiErrs = extractApiErrors(err);
      if (Object.keys(apiErrs).length) {
        setPayErrors(apiErrs);
        notify?.('Please fix the highlighted fields', 'error');
      } else {
        notify?.('Failed to record payment', 'error');
      }
    }
    finally { setSavingPay(false); }
  };

  const openPayForm = (inv) => {
    setPayInvoice(inv);
    setPayForm({
      amount: inv.balance_due,
      method: 'bank',
      reference: '',
      notes: '',
      payment_date: new Date().toISOString().split('T')[0],
    });
    setShowPayForm(true);
  };

  const openEditVendor = (v) => {
    setEditVendor(v);
    setVendorForm({
      name: v.name, contact_person: v.contact_person || '', phone: v.phone || '',
      email: v.email || '', address: v.address || '', kra_pin: v.kra_pin || '',
      payment_terms_days: v.payment_terms_days, bank_name: v.bank_name || '',
      bank_account: v.bank_account || '', category: v.category, notes: v.notes || '',
    });
    setShowVendorForm(true);
  };

  // Auto-compute due date from vendor payment terms when vendor is selected
  const handleInvVendorChange = (vendorId) => {
    inf('vendor', vendorId);
    if (vendorId) {
      const v = vendors.find(v => String(v.id) === String(vendorId));
      if (v?.payment_terms_days && invForm.invoice_date) {
        const due = new Date(invForm.invoice_date);
        due.setDate(due.getDate() + Number(v.payment_terms_days));
        inf('due_date', due.toISOString().split('T')[0]);
      }
    }
  };

  const filteredVendors = vendors.filter(v =>
    !vendorFilter ||
    v.name.toLowerCase().includes(vendorFilter.toLowerCase()) ||
    v.vendor_code?.toLowerCase().includes(vendorFilter.toLowerCase()) ||
    v.contact_person?.toLowerCase().includes(vendorFilter.toLowerCase())
  );

  const filteredInvoices = invoices.filter(i => {
    if (invFilter.status && i.status !== invFilter.status) return false;
    if (invFilter.vendor && String(i.vendor) !== String(invFilter.vendor)) return false;
    return true;
  });

  const unpaidTotal = invoices.filter(i => i.status !== 'paid').reduce((s, i) => s + parseFloat(i.balance_due || 0), 0);

  const tabs = [
    { id: 'vendors',  label: '🏭 Suppliers' },
    { id: 'invoices', label: '📄 AP Invoices' },
    { id: 'ageing',   label: '⏱ Ageing Report' },
  ];

  return (
    <div style={{ padding: '0 0 40px' }}>
      <PageHeader icon="🏭" title="Vendors & Accounts Payable"
        sub="Supplier profiles · Invoices · Payments · AP Ageing"
        action={canEdit ? (
          tab === 'vendors'
            ? <button onClick={() => { setEditVendor(null); setVendorForm(blankVendor); setShowVendorForm(true); }} style={btnPrimary}>+ Add Vendor</button>
            : tab === 'invoices'
            ? <button onClick={() => setShowInvForm(true)} style={btnPrimary}>+ Record Invoice</button>
            : null
        ) : null}
      />
      <div style={{ padding: '0 20px 12px' }}><TabBar tabs={tabs} active={tab} onChange={setTab} /></div>

      {/* Outstanding summary banner */}
      {unpaidTotal > 0 && (
        <div style={{
          margin: '0 20px 16px', padding: '12px 18px',
          background: `linear-gradient(135deg, ${red}10, ${orange}08)`,
          border: `1px solid ${red}28`, borderRadius: 10,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ color: 'var(--textMuted)', fontSize: 13 }}>
            📊 Total outstanding across <strong style={{ color: 'var(--text)' }}>{invoices.filter(i => i.status !== 'paid').length}</strong> unpaid invoices
          </span>
          <span style={{ fontWeight: 800, color: red, fontSize: 18 }}>{fmtKES(unpaidTotal)}</span>
        </div>
      )}

      {/* ══ VENDOR FORM MODAL ═══════════════════════════════════════════ */}
      {showVendorForm && (
        <Modal onClose={() => { setShowVendorForm(false); setVendorErrors({}); }} width={680}>
          <ModalHeader
            icon={editVendor ? '✏️' : '🏭'}
            title={editVendor ? `Edit Supplier` : 'New Supplier Profile'}
            subtitle={editVendor ? `Editing: ${editVendor.name}` : 'Only the supplier name is required — add more details any time.'}
            onClose={() => { setShowVendorForm(false); setVendorErrors({}); }}
          />
          <div style={{ padding: '20px 24px', maxHeight: '66vh', overflowY: 'auto' }}>
            <ErrorBanner errors={vendorErrors} />

            {/* ── Supplier Name ── */}
            <Field label="Supplier / Company Name" required error={vendorErrors.name} hint={!vendorErrors.name ? 'As it appears on official invoices' : undefined}>
              <input value={vendorForm.name} onChange={e => { vf('name', e.target.value); setVendorErrors(p => ({ ...p, name: '' })); }}
                placeholder="e.g. Nairobi Foods Ltd" autoFocus
                style={{ ...iStyle, fontSize: 16, fontWeight: 700, ...(vendorErrors.name ? { borderColor: '#ef4444', background: 'rgba(239,68,68,0.04)' } : {}) }} />
            </Field>

            {/* ── Category chips ── */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--textMuted)', textTransform: 'uppercase', marginBottom: 8 }}>Category</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {VENDOR_CATS.map(c => {
                  const active = vendorForm.category === c.value;
                  return (
                    <button key={c.value} onClick={() => vf('category', c.value)} style={{
                      padding: '7px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      border: `1.5px solid ${active ? gold : 'var(--borderMid)'}`,
                      background: active ? gold + '20' : 'var(--bgCard)',
                      color: active ? gold : 'var(--textMuted)',
                      transition: 'all 0.15s',
                    }}>{c.label}</button>
                  );
                })}
              </div>
            </div>

            {/* ── Payment Terms quick picks ── */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--textMuted)', textTransform: 'uppercase', marginBottom: 8 }}>
                Payment Terms <span style={{ color: 'var(--textMuted)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(days after invoice)</span>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {[7, 14, 30, 45, 60].map(d => {
                  const active = String(vendorForm.payment_terms_days) === String(d);
                  return (
                    <button key={d} onClick={() => { vf('payment_terms_days', d); setVendorErrors(p => ({ ...p, payment_terms_days: '' })); }} style={{
                      padding: '7px 16px', borderRadius: 20, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                      border: `1.5px solid ${active ? gold : 'var(--borderMid)'}`,
                      background: active ? gold + '20' : 'var(--bgCard)',
                      color: active ? gold : 'var(--textMuted)',
                    }}>{d}</button>
                  );
                })}
                <input type="number" min="0" value={vendorForm.payment_terms_days}
                  onChange={e => { vf('payment_terms_days', e.target.value); setVendorErrors(p => ({ ...p, payment_terms_days: '' })); }}
                  placeholder="Custom"
                  style={{ ...iStyle, width: 90, padding: '7px 10px', fontSize: 13, ...(vendorErrors.payment_terms_days ? { borderColor: '#ef4444' } : {}) }} />
              </div>
              {vendorErrors.payment_terms_days && <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>{vendorErrors.payment_terms_days}</div>}
            </div>

            {/* ── Contact Details ── */}
            <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '16px 18px', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid rgba(201,168,76,0.15)' }}>
                <span>👤</span>
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.09em', color: gold, textTransform: 'uppercase' }}>Contact Details</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FInput label="Contact Person" value={vendorForm.contact_person} onChange={e => vf('contact_person', e.target.value)} placeholder="e.g. Jane Mwangi" error={vendorErrors.contact_person} />
                <FInput label="Phone" type="tel" value={vendorForm.phone} onChange={e => { vf('phone', e.target.value); setVendorErrors(p => ({ ...p, phone: '' })); }} placeholder="0712 345 678" error={vendorErrors.phone} />
                <FInput label="Email" type="email" value={vendorForm.email} onChange={e => { vf('email', e.target.value); setVendorErrors(p => ({ ...p, email: '' })); }} placeholder="accounts@vendor.co.ke" error={vendorErrors.email} />
                <FInput label="KRA PIN" hint={!vendorErrors.kra_pin ? 'For VAT / tax compliance' : undefined} value={vendorForm.kra_pin} onChange={e => vf('kra_pin', e.target.value)} placeholder="A001234567K" error={vendorErrors.kra_pin} />
                <FInput label="Physical Address" span={2} value={vendorForm.address} onChange={e => vf('address', e.target.value)} placeholder="e.g. Mombasa Road, Industrial Area, Nairobi" error={vendorErrors.address} />
              </div>
            </div>

            {/* ── Banking Details ── */}
            <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '16px 18px', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid rgba(201,168,76,0.15)' }}>
                <span>🏦</span>
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.09em', color: gold, textTransform: 'uppercase' }}>Banking Details</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FInput label="Bank Name" value={vendorForm.bank_name} onChange={e => vf('bank_name', e.target.value)} placeholder="e.g. Equity Bank" error={vendorErrors.bank_name} />
                <FInput label="Account Number" value={vendorForm.bank_account} onChange={e => vf('bank_account', e.target.value)} placeholder="0123456789" error={vendorErrors.bank_account} />
              </div>
            </div>

            {/* ── Notes ── */}
            <Field label="Internal Notes" hint="Not printed on any documents">
              <textarea value={vendorForm.notes} onChange={e => vf('notes', e.target.value)}
                placeholder="e.g. Preferred for fresh produce — call after 8am, weekend deliveries available..."
                rows={3} style={{ ...iStyle, resize: 'vertical' }} />
            </Field>
          </div>
          <ModalFooter onSave={saveVendor} onCancel={() => { setShowVendorForm(false); setVendorErrors({}); }}
            saveLabel={editVendor ? '✓ Save Changes' : '+ Add Supplier'} saving={savingVendor}
            errorCount={Object.values(vendorErrors).filter(Boolean).length} />
        </Modal>
      )}

      {/* ══ INVOICE FORM MODAL ══════════════════════════════════════════ */}
      {showInvForm && (
        <Modal onClose={() => { setShowInvForm(false); setInvErrors({}); }} width={540}>
          <ModalHeader icon="📄" title="Record AP Invoice"
            subtitle="Log a supplier invoice for payment tracking and AP ageing"
            onClose={() => { setShowInvForm(false); setInvErrors({}); }} />
          <div style={{ padding: '20px 24px' }}>
            <ErrorBanner errors={invErrors} />
            <Field label="Supplier / Vendor" required error={invErrors.vendor} hint={!invErrors.vendor ? 'Who issued this invoice?' : undefined}>
              <select value={invForm.vendor} onChange={e => { handleInvVendorChange(e.target.value); setInvErrors(p => ({ ...p, vendor: '' })); }}
                style={{ ...(invErrors.vendor ? { ...iStyle, borderColor: '#ef4444', background: 'rgba(239,68,68,0.04)' } : iStyle), cursor: 'pointer' }}>
                <option value="">— Select a vendor —</option>
                {vendors.filter(v => v.is_active).map(v => (
                  <option key={v.id} value={v.id}>{v.vendor_code} · {v.name}</option>
                ))}
              </select>
            </Field>

            {/* Vendor quick-info hint */}
            {invForm.vendor && (() => {
              const v = vendors.find(x => String(x.id) === String(invForm.vendor));
              return v ? (
                <div style={{ background: blue+'10', border:`1px solid ${blue}25`, borderRadius:8, padding:'8px 12px', marginBottom:14, fontSize:12, color:blue, display:'flex', gap:12, flexWrap:'wrap' }}>
                  <span>ℹ️ Net <strong>{v.payment_terms_days||30} days</strong></span>
                  {v.contact_person && <span>👤 {v.contact_person}</span>}
                  {v.phone && <span>📞 {v.phone}</span>}
                </div>
              ) : null;
            })()}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FInput label="Invoice Number" required span={2}
                value={invForm.invoice_number} onChange={e => { inf('invoice_number', e.target.value); setInvErrors(p => ({ ...p, invoice_number: '' })); }}
                placeholder="e.g. INV-2025-00123" hint={!invErrors.invoice_number ? "As printed on the supplier's invoice" : undefined}
                error={invErrors.invoice_number} />
              <FInput label="Amount (KES)" required type="number" min="0" step="0.01"
                value={invForm.amount} onChange={e => { inf('amount', e.target.value); setInvErrors(p => ({ ...p, amount: '' })); }}
                placeholder="0.00" error={invErrors.amount} />
              <div />
              <FInput label="Invoice Date" required type="date"
                value={invForm.invoice_date} onChange={e => { inf('invoice_date', e.target.value); setInvErrors(p => ({ ...p, invoice_date: '' })); }}
                error={invErrors.invoice_date} />
              <FInput label="Due Date" required type="date"
                hint={!invErrors.due_date ? 'Auto-set from payment terms' : undefined}
                value={invForm.due_date} onChange={e => { inf('due_date', e.target.value); setInvErrors(p => ({ ...p, due_date: '' })); }}
                error={invErrors.due_date} />
              <FInput label="Description / Purpose" span={2}
                value={invForm.description} onChange={e => inf('description', e.target.value)}
                placeholder="e.g. Fresh produce delivery – week 12" error={invErrors.description} />
            </div>
          </div>
          <ModalFooter onSave={saveInvoice} onCancel={() => { setShowInvForm(false); setInvErrors({}); }} saveLabel="Record Invoice" saving={savingInv}
            errorCount={Object.values(invErrors).filter(Boolean).length} />
        </Modal>
      )}

      {/* ══ PAYMENT FORM MODAL ══════════════════════════════════════════ */}
      {showPayForm && payInvoice && (
        <Modal onClose={() => { setShowPayForm(false); setPayErrors({}); }} width={460}>
          <ModalHeader icon="💳" title="Record Payment"
            subtitle={`${payInvoice.vendor_name} · Invoice ${payInvoice.invoice_number}`}
            onClose={() => { setShowPayForm(false); setPayErrors({}); }} />
          <div style={{ padding: '16px 24px' }}>
            {/* Balance callout */}
            <div style={{ background:`${red}10`, border:`1px solid ${red}28`, borderRadius:10, padding:'14px 18px', marginBottom:18, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <div style={{ color:'var(--textMuted)', fontSize:10, letterSpacing:'0.08em', marginBottom:4 }}>BALANCE DUE</div>
                <div style={{ fontWeight:800, fontSize:24, color:red }}>{fmtKES(payInvoice.balance_due)}</div>
              </div>
              <div style={{ textAlign:'right' }}>
                <div style={{ color:'var(--textMuted)', fontSize:10, letterSpacing:'0.08em', marginBottom:4 }}>ORIGINAL INVOICE</div>
                <div style={{ fontWeight:600, fontSize:15 }}>{fmtKES(payInvoice.amount)}</div>
              </div>
            </div>

            <ErrorBanner errors={payErrors} />

            <FInput label="Amount to Pay (KES)" required type="number" min="0" step="0.01"
              value={payForm.amount} onChange={e => { pf('amount', e.target.value); setPayErrors(p => ({ ...p, amount: '' })); }}
              hint={!payErrors.amount ? `Max: ${fmtKES(payInvoice.balance_due)} — partial payments allowed` : undefined}
              error={payErrors.amount} />

            <FInput label="Payment Date" type="date" required
              value={payForm.payment_date || new Date().toISOString().split('T')[0]}
              onChange={e => { pf('payment_date', e.target.value); setPayErrors(p => ({ ...p, payment_date: '' })); }}
              error={payErrors.payment_date} />

            <Field label="Payment Method" required error={payErrors.method}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginTop:2 }}>
                {PAYMENT_METHODS.map(m => (
                  <button key={m.value} onClick={() => { pf('method', m.value); setPayErrors(p => ({ ...p, method: '' })); }} style={{
                    padding:'10px 8px', borderRadius:8, border:'1.5px solid',
                    borderColor: payErrors.method ? '#ef4444' : payForm.method===m.value ? gold : 'var(--borderMid)',
                    background: payForm.method===m.value ? gold+'18' : 'var(--bgCard)',
                    color: payForm.method===m.value ? gold : 'var(--textMuted)',
                    cursor:'pointer', fontSize:12, fontWeight:600, textAlign:'center', transition:'all 0.12s',
                  }}>{m.label}</button>
                ))}
              </div>
            </Field>

            <div style={{ marginTop:12 }}>
              <FInput label="Reference Number"
                value={payForm.reference} onChange={e => pf('reference', e.target.value)}
                placeholder={
                  payForm.method==='mpesa'  ? 'M-Pesa code e.g. QA12BCD45' :
                  payForm.method==='cheque' ? 'Cheque number' :
                  payForm.method==='bank'   ? 'Bank reference / transaction ID' : 'Reference'
                } />
            </div>
          </div>
          <ModalFooter onSave={recordPayment} onCancel={() => { setShowPayForm(false); setPayErrors({}); }} saveLabel="✓ Confirm Payment" saving={savingPay}
            errorCount={Object.values(payErrors).filter(Boolean).length} />
        </Modal>
      )}

      {/* ══ VENDORS TAB ═════════════════════════════════════════════════ */}
      {tab === 'vendors' && (
        <div style={{ padding:'0 20px' }}>
          <div style={{ position:'relative', marginBottom:16, maxWidth:360 }}>
            <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'var(--textMuted)', pointerEvents:'none' }}>🔍</span>
            <input placeholder="Search by name, code or contact…" value={vendorFilter} onChange={e => setVendorFilter(e.target.value)}
              style={{ ...iStyle, paddingLeft:34 }} />
          </div>

          {filteredVendors.length === 0 ? <Empty text="No vendors yet. Add your first supplier above." /> : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(330px,1fr))', gap:14 }}>
              {filteredVendors.map(v => {
                const catObj = VENDOR_CATS.find(c => c.value === v.category);
                const hasBalance = parseFloat(v.outstanding_balance||0) > 0;
                return (
                  <div key={v.id} style={{ background:'var(--surface)', borderRadius:14, overflow:'hidden',
                    border:'1px solid rgba(255,255,255,0.06)', boxShadow:'0 2px 10px rgba(0,0,0,0.12)',
                    transition:'transform 0.14s,box-shadow 0.14s' }}
                    onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow='0 8px 24px rgba(0,0,0,0.2)';}}
                    onMouseLeave={e=>{e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow='0 2px 10px rgba(0,0,0,0.12)';}}>
                    {/* Accent bar based on outstanding */}
                    <div style={{ height:3, background:hasBalance?red:green }} />
                    <div style={{ padding:'14px 16px' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:800, color:'var(--text)', fontSize:15, marginBottom:6 }}>{v.name}</div>
                          <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                            <span style={{ fontSize:10, fontFamily:'monospace', color:'var(--textMuted)', background:'rgba(255,255,255,0.06)', padding:'2px 7px', borderRadius:4 }}>{v.vendor_code}</span>
                            <span style={{ background:gold+'18', color:gold, borderRadius:20, padding:'2px 9px', fontSize:10, fontWeight:700, border:`1px solid ${gold}30` }}>
                              {catObj?.label?.replace(/^[^\w]*/, '') || v.category}
                            </span>
                          </div>
                        </div>
                        <div style={{ textAlign:'right', marginLeft:12, padding:'8px 12px', borderRadius:10, background:hasBalance?`${red}12`:`${green}10` }}>
                          {hasBalance ? (
                            <><div style={{ color:red, fontWeight:800, fontSize:16 }}>{fmtKES(v.outstanding_balance)}</div>
                            <div style={{ color:red, fontSize:10, marginTop:1 }}>Outstanding</div></>
                          ) : (
                            <><div style={{ color:green, fontSize:18 }}>✓</div>
                            <div style={{ color:green, fontSize:10, marginTop:1 }}>Clear</div></>
                          )}
                        </div>
                      </div>
                      {/* Contact grid */}
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6, marginBottom:10 }}>
                        {[
                          v.contact_person && ['👤', v.contact_person],
                          v.phone          && ['📞', v.phone],
                          v.email          && ['📧', v.email],
                          v.payment_terms_days && ['📅', `Net ${v.payment_terms_days} days`],
                        ].filter(Boolean).map(([icon,val]) => (
                          <div key={val} style={{ fontSize:11, color:'var(--textMuted)', display:'flex', gap:5, alignItems:'center',
                            background:'rgba(255,255,255,0.03)', borderRadius:6, padding:'5px 8px' }}>
                            <span>{icon}</span><span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{val}</span>
                          </div>
                        ))}
                      </div>
                      {v.kra_pin && (
                        <div style={{ fontSize:11, color:'var(--textMuted)', marginBottom:8, display:'flex', gap:5 }}>
                          <span>🆔</span><span>KRA: {v.kra_pin}</span>
                          {v.bank_name && <><span style={{ color:'rgba(255,255,255,0.2)' }}>|</span><span>🏦 {v.bank_name}</span></>}
                        </div>
                      )}
                      {v.notes && (
                        <div style={{ padding:'6px 10px', background:'rgba(255,255,255,0.03)', borderRadius:6, fontSize:11, color:'var(--textMuted)', fontStyle:'italic', marginBottom:10 }}>
                          💭 {v.notes.slice(0,80)}{v.notes.length>80?'…':''}
                        </div>
                      )}
                      {canEdit && (
                        <button onClick={() => openEditVendor(v)} style={{ ...btnSm, width:'100%', fontSize:11 }}>✏️ Edit Vendor</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══ INVOICES TAB ════════════════════════════════════════════════ */}
      {tab === 'invoices' && (
        <div style={{ padding:'0 20px' }}>
          <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
            <select value={invFilter.status} onChange={e => setInvFilter(p=>({...p,status:e.target.value}))} style={{ ...iStyle, width:170 }}>
              <option value="">All Statuses</option>
              {['unpaid','partial','paid','disputed'].map(s => (
                <option key={s} value={s}>{statusIcon[s]} {s.charAt(0).toUpperCase()+s.slice(1)}</option>
              ))}
            </select>
            <select value={invFilter.vendor} onChange={e => setInvFilter(p=>({...p,vendor:e.target.value}))} style={{ ...iStyle, width:220 }}>
              <option value="">All Vendors</option>
              {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
            {(invFilter.status||invFilter.vendor) && (
              <button onClick={() => setInvFilter({status:'',vendor:''})} style={{ ...btnSm, fontSize:12 }}>✕ Clear</button>
            )}
          </div>

          {filteredInvoices.length === 0 ? <Empty text="No invoices found" /> : (
            <Card>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                  <thead>
                    <tr style={{ background:'#111' }}>
                      {['Vendor','Invoice #','Invoice Date','Due Date','Amount','Paid','Balance','Status',''].map(h => (
                        <th key={h} style={{ padding:'10px 12px', textAlign:['Amount','Paid','Balance'].includes(h)?'right':'left', color:gold, fontSize:11, letterSpacing:'0.05em', fontWeight:700, whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoices.map((inv,i) => (
                      <tr key={inv.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)', background:i%2===0?'transparent':'rgba(255,255,255,0.01)' }}>
                        <td style={{ padding:'10px 12px', fontWeight:600 }}>{inv.vendor_name}</td>
                        <td style={{ padding:'10px 12px', color:gold, fontFamily:'monospace', fontSize:12 }}>{inv.invoice_number}</td>
                        <td style={{ padding:'10px 12px', color:'var(--textMuted)', fontSize:12 }}>{inv.invoice_date}</td>
                        <td style={{ padding:'10px 12px' }}>
                          <span style={{ color:inv.days_overdue>0?red:'var(--textMuted)', fontSize:12 }}>{inv.due_date}</span>
                          {inv.days_overdue>0 && <span style={{ marginLeft:6, fontSize:10, background:red+'22', color:red, borderRadius:4, padding:'1px 5px' }}>{inv.days_overdue}d late</span>}
                        </td>
                        <td style={{ padding:'10px 12px', textAlign:'right' }}>{fmtKES(inv.amount)}</td>
                        <td style={{ padding:'10px 12px', textAlign:'right', color:green }}>{fmtKES(inv.amount_paid)}</td>
                        <td style={{ padding:'10px 12px', textAlign:'right', fontWeight:700, color:parseFloat(inv.balance_due)>0?red:green }}>{fmtKES(inv.balance_due)}</td>
                        <td style={{ padding:'10px 12px' }}>
                          <span style={{ background:statusColor[inv.status]+'22', color:statusColor[inv.status], borderRadius:20, padding:'3px 10px', fontSize:11, fontWeight:700 }}>
                            {statusIcon[inv.status]} {inv.status}
                          </span>
                        </td>
                        <td style={{ padding:'10px 12px' }}>
                          {canEdit && inv.status !== 'paid' && (
                            <button onClick={() => openPayForm(inv)} style={{ ...btnSm, fontSize:11, padding:'4px 12px', background:green+'18', color:green, border:`1px solid ${green}44`, borderRadius:6 }}>💳 Pay</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* ══ AGEING TAB ══════════════════════════════════════════════════ */}
      {tab === 'ageing' && (
        <div style={{ padding:'0 20px' }}>
          {!ageing ? (
            <div style={{ textAlign:'center', color:'var(--textMuted)', padding:60, fontSize:14 }}>⏳ Loading ageing report…</div>
          ) : (
            <>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:14, marginBottom:20 }}>
                {[
                  { label:'Current',    key:'current', color:green,    icon:'✅' },
                  { label:'1–30 Days',  key:'1_30',    color:gold,     icon:'⚠️' },
                  { label:'31–60 Days', key:'31_60',   color:orange,   icon:'🔶' },
                  { label:'61–90 Days', key:'61_90',   color:red,      icon:'🔴' },
                  { label:'Over 90',    key:'over_90', color:'#c0392b', icon:'🚨' },
                ].map(b => (
                  <div key={b.key} style={{ background:'var(--surface)', borderRadius:12, padding:'14px 16px', borderTop:`3px solid ${b.color}` }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                      <span style={{ color:'var(--textMuted)', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em' }}>{b.label}</span>
                      <span style={{ fontSize:14 }}>{b.icon}</span>
                    </div>
                    <div style={{ fontWeight:800, fontSize:16, color:b.color }}>{fmtKES(ageing.totals[b.key]||0)}</div>
                  </div>
                ))}
              </div>
              <Card>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingBottom:14, borderBottom:`2px solid ${gold}33` }}>
                  <span style={{ fontWeight:700, color:gold }}>📋 Invoice Detail</span>
                  <div>
                    <span style={{ color:'var(--textMuted)', fontSize:12, marginRight:10 }}>Total Outstanding</span>
                    <span style={{ fontWeight:800, color:red, fontSize:20 }}>{fmtKES(ageing.total_outstanding)}</span>
                  </div>
                </div>
                <div style={{ overflowX:'auto', marginTop:12 }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                    <thead>
                      <tr style={{ background:'#111' }}>
                        {['Vendor','Invoice #','Due Date','Balance Due','Days Overdue','Bucket'].map(h => (
                          <th key={h} style={{ padding:'10px 12px', textAlign:'left', color:gold, fontSize:11, letterSpacing:'0.05em' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {ageing.rows.map((row,i) => (
                        <tr key={i} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                          <td style={{ padding:'10px 12px', fontWeight:600 }}>{row.vendor}</td>
                          <td style={{ padding:'10px 12px', color:gold, fontFamily:'monospace', fontSize:12 }}>{row.invoice_number}</td>
                          <td style={{ padding:'10px 12px', color:'var(--textMuted)', fontSize:12 }}>{row.due_date}</td>
                          <td style={{ padding:'10px 12px', fontWeight:700, color:red }}>{fmtKES(row.balance_due)}</td>
                          <td style={{ padding:'10px 12px' }}>
                            {row.days_overdue===0
                              ? <span style={{ color:green }}>✓ Current</span>
                              : <span style={{ color:row.days_overdue>60?red:orange, fontWeight:600 }}>{row.days_overdue} days</span>}
                          </td>
                          <td style={{ padding:'10px 12px' }}>
                            <span style={{ fontSize:11, borderRadius:20, padding:'2px 10px', fontWeight:600,
                              background:row.bucket==='current'?green+'18':row.bucket==='over_90'?red+'18':orange+'18',
                              color:row.bucket==='current'?green:row.bucket==='over_90'?red:orange }}>
                              {row.bucket?.replace('_','–')}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}
        </div>
      )}
    </div>
  );
}
